import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import { Skeleton, Card, CardContent, CardHeader, CardTitle, Button } from '@org/ui';
import { FileWarning, UserX, CheckCircle, Clock, AlertTriangle, BarChart3 } from 'lucide-react';

export const Route = createFileRoute('/admin/')({
  component: AdminOverview,
});

function AdminOverview() {
  const { t } = useTranslation('community');

  const { data: pendingReports, isLoading: loadingPending } =
    communityTrpc.moderation.listReports.useQuery({ status: 'pending', limit: 5 });

  const { data: reviewedReports, isLoading: loadingReviewed } =
    communityTrpc.moderation.listReports.useQuery({ status: 'reviewed', limit: 1 });

  const { data: resolvedReports, isLoading: loadingResolved } =
    communityTrpc.moderation.listReports.useQuery({ status: 'resolved', limit: 1 });

  const { data: bannedUsers, isLoading: loadingBanned } =
    communityTrpc.moderation.listBannedUsers.useQuery({ limit: 5 });

  const stats = [
    {
      label: t('admin.moderation.pending'),
      value: pendingReports?.total ?? 0,
      icon: Clock,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      loading: loadingPending,
      href: '/admin/moderation?status=pending',
    },
    {
      label: t('admin.moderation.reviewed'),
      value: reviewedReports?.total ?? 0,
      icon: AlertTriangle,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      loading: loadingReviewed,
      href: '/admin/moderation?status=reviewed',
    },
    {
      label: t('admin.moderation.resolved'),
      value: resolvedReports?.total ?? 0,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      loading: loadingResolved,
      href: '/admin/moderation?status=resolved',
    },
    {
      label: t('admin.bans.title'),
      value: bannedUsers?.total ?? 0,
      icon: UserX,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      loading: loadingBanned,
      href: '/admin/bans',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('admin.overview.title')}</h1>
          <p className="text-muted-foreground">{t('admin.overview.subtitle')}</p>
        </div>
        <Link to="/admin/analytics">
          <Button variant="outline" className="gap-2">
            <BarChart3 className="h-4 w-4" aria-hidden="true" />
            {t('admin.overview.viewAnalytics')}
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Link key={stat.label} to={stat.href}>
            <Card className="transition-shadow hover:shadow-md">
              <CardContent className="flex items-center gap-4 p-6">
                <div className={`rounded-full p-3 ${stat.bgColor}`}>
                  <stat.icon className={`h-6 w-6 ${stat.color}`} aria-hidden="true" />
                </div>
                <div>
                  {stat.loading ? (
                    <Skeleton className="h-8 w-12" />
                  ) : (
                    <p className="text-2xl font-bold">{stat.value}</p>
                  )}
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Reports */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileWarning className="h-5 w-5" aria-hidden="true" />
              {t('admin.overview.recentReports')}
            </CardTitle>
            <Link
              to="/admin/moderation"
              className="text-sm text-primary hover:underline"
            >
              {t('admin.overview.viewAll')}
            </Link>
          </CardHeader>
          <CardContent>
            {loadingPending ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : pendingReports?.reports.length === 0 ? (
              <p className="py-4 text-center text-muted-foreground">
                {t('admin.overview.noPendingReports')}
              </p>
            ) : (
              <div className="space-y-3">
                {pendingReports?.reports.slice(0, 5).map((report) => (
                  <Link
                    key={report.reportId}
                    to="/admin/moderation"
                    search={{ reportId: report.reportId }}
                    className="flex items-center justify-between rounded-lg border border-border p-3 transition-colors hover:bg-muted/50"
                  >
                    <div>
                      <p className="font-medium capitalize">
                        {report.targetType} - {report.reason.replace('_', ' ')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        by {report.reporter?.displayName || report.reporter?.firstName || 'Unknown'}
                      </p>
                    </div>
                    <span className={`rounded-full px-2 py-1 text-xs font-medium ${
                      report.status === 'pending'
                        ? 'bg-amber-100 text-amber-700'
                        : report.status === 'reviewed'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {report.status}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Bans */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <UserX className="h-5 w-5" aria-hidden="true" />
              {t('admin.overview.recentBans')}
            </CardTitle>
            <Link
              to="/admin/bans"
              className="text-sm text-primary hover:underline"
            >
              {t('admin.overview.viewAll')}
            </Link>
          </CardHeader>
          <CardContent>
            {loadingBanned ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : bannedUsers?.bans.length === 0 ? (
              <p className="py-4 text-center text-muted-foreground">
                {t('admin.overview.noActiveBans')}
              </p>
            ) : (
              <div className="space-y-3">
                {bannedUsers?.bans.slice(0, 5).map((ban) => (
                  <div
                    key={ban.banId}
                    className="flex items-center justify-between rounded-lg border border-border p-3"
                  >
                    <div>
                      <p className="font-medium">
                        {ban.user?.displayName || ban.user?.firstName || 'Unknown User'}
                      </p>
                      <p className="text-sm text-muted-foreground line-clamp-1">
                        {ban.reason}
                      </p>
                    </div>
                    <span className={`rounded-full px-2 py-1 text-xs font-medium ${
                      ban.expiresAt
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {ban.expiresAt ? t('admin.overview.temporary') : t('admin.overview.permanent')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
